import { Presentation, Slide } from '@features/powerpoint';
import { BuildReportOptions } from './build-presentation';
import { visibleSlide } from '@features/executive-report/slides';
import { titleSlide } from '@features/executive-report/slides/v2/title';
import { agendaSlide } from '@features/executive-report/slides/v2/agenda';
import { coverSlide } from '@features/executive-report/slides/v2/cover';
import { salesCampaignOverviewSlide } from '@features/executive-report/slides/v2/sales-campaign-overview';
import { salesCampaignOverviewSlide2 } from '@features/executive-report/slides/v2/sales-campaign-overview-2';
import { buildNavbarText } from '@features/executive-report/slides/v2/components/navbar-texts';
import { visitOverviewSlide } from '@features/executive-report/slides/v2/visit-overview';
import { buildKeyDriversCards } from '../../visitation/slides/v2/sales-campaign-overview-2/build-key-drivers-cards';
import { navbarSections } from '../../visitation/domain/navbar-sections';
import { salesDimensionsSlides } from '../slides/v2/sales-dimensions';
import { visitDimensionsSlides } from '../slides/v2/visit-dimensions';
import { howWeDoItSlide } from '@features/executive-report/slides/v2/how-we-do-it';
import { optimizationChannelsSlide } from '@features/executive-report/slides/v2/optimization-channels';
import { countSignificantDimensions } from '../domain/significant-dimensions';
import { campaignInsightsSlides } from '../../visitation/slides/v2/campaign-insights-slides';

export type BuildReportOptionsV2 = Extract<
  BuildReportOptions,
  { designVersion: 'v2' }
>;

const buildSalesSlides = (options: BuildReportOptionsV2): Slide[] => {
  const keyDriversCards = buildKeyDriversCards({
    penetrationRate: {
      total: options.report.penetrationLift,
      exposed: options.report.penetration,
      control: options.report.controlPenetration,
    },
    transactionSize: {
      total: options.report.transactionSizeLift,
      exposed: options.report.transactionSize,
      control: options.report.controlTransactionSize,
    },
    transactionRate: {
      total: options.report.transactionRateLift,
      exposed: options.report.transactionRate,
      control: options.report.controlTransactionRate,
    },
  });

  return [
    ...visibleSlide({
      slide: coverSlide('Sales Lift'),
      visible: options.customFields.includedSlides.salesLift.cover,
    }),
    ...visibleSlide({
      slide: salesCampaignOverviewSlide({
        navbarText: buildNavbarText(navbarSections, 'Overview'),
        adSpend: options.campaign.adSpend,
        flightDates: [options.campaign.startDate, options.campaign.endDate],
        measurementPeriod: [
          options.campaign.startDate,
          options.campaign.endDate,
        ],
        uniqueUsers: options.visitMetrics.uniqueUsers,
        impressions: options.visitMetrics.impressions,
        sections: [
          {
            name: 'Who',
            count: countSignificantDimensions(options.report.dimensions, 'aud'),
            dimensionName: 'Audience',
          },
          {
            name: 'What',
            count: countSignificantDimensions(
              options.report.dimensions,
              'creative'
            ),
            dimensionName: 'Creative',
          },
          {
            name: 'Where',
            count: countSignificantDimensions(
              options.report.dimensions,
              'channel'
            ),
            dimensionName: 'Channel',
          },
        ],
      }),
      visible: options.customFields.includedSlides.salesLift.campaignOverview,
    }),
    ...visibleSlide({
      slide: salesCampaignOverviewSlide2({
        navbarText: buildNavbarText(navbarSections, 'Overview'),
        incrementalLift: options.report.incrementalSalesLift,
        incrementalSales: options.report.projectedIncrementalSales,
        iroas: options.iroas,
        objectives: options.customFields.salesObjectives.map(o => o.value),
        keyDrivers: keyDriversCards,
      }),
      visible: options.customFields.includedSlides.salesLift.campaignOverview2,
    }),
    ...salesDimensionsSlides(options),
    ...visibleSlide({
      slide: optimizationChannelsSlide({
        midCampaignSummary: options.midCampaignReport
          ? {
              lift: options.midCampaignReport.projectedIncrementalSales ?? 0,
              totalIncrementality:
                options.midCampaignReport.incrementalSalesLift ?? 0,
              transactionRateSales:
                options.midCampaignReport.transactionRate ?? 0,
              transactionRateLift:
                options.midCampaignReport.transactionRateLift ?? 0,
              transactionSizeSales:
                options.midCampaignReport.transactionSize ?? 0,
              transactionSizeLift:
                options.midCampaignReport.transactionSizeLift ?? 0,
              penetrationSales: options.midCampaignReport.penetration ?? 0,
              penetrationLift: options.midCampaignReport.penetrationLift ?? 0,
            }
          : null,
        endCampaignSummary: {
          lift: options.report.projectedIncrementalSales ?? 0,
          totalIncrementality: options.report.incrementalSalesLift ?? 0,
          transactionRateSales: options.report.transactionRate ?? 0,
          transactionRateLift: options.report.transactionRateLift ?? 0,
          transactionSizeSales: options.report.transactionSize ?? 0,
          transactionSizeLift: options.report.transactionSizeLift ?? 0,
          penetrationSales: options.report.penetration ?? 0,
          penetrationLift: options.report.penetrationLift ?? 0,
        },
      }),
      visible:
        options.customFields.includedSlides.salesLift.optimizationSummary,
    }),
  ];
};

const buildVisitsSlides = (options: BuildReportOptionsV2): Slide[] => {
  if (!options.report.hasVisitMetrics) return [];

  return [
    ...visibleSlide({
      slide: coverSlide('Visit Lift'),
      visible: options.customFields.includedSlides.visitLift.cover,
    }),
    ...visibleSlide({
      slide: visitOverviewSlide({
        navbarText: buildNavbarText(navbarSections, 'Overview'),
        brandObjective: options.customFields.footTrafficObjectives
          .map(o => o.value)
          .join(' '),
        cpiv: options.costPerIncrementalVisit,
        incrementalVisitLift: options.visitMetrics.incrementalLift,
        totalIncrementalVisits: options.visitMetrics.incrementalVisits,
        totalProjectedVisits: options.visitMetrics.projectedVisits,
        uniqueUsers: options.visitMetrics.uniqueUsers,
        impressions: options.visitMetrics.impressions,
      }),
      visible: options.customFields.includedSlides.visitLift.overview,
    }),
    ...visitDimensionsSlides(options),
  ];
};

export const buildSlidesV2 = (
  options: BuildReportOptionsV2
): Presentation['slides'] => [
  ...visibleSlide({
    slide: titleSlide(
      options.campaign,
      options.brandLogo,
      options.customFields.campaignName
    ),
    visible: options.customFields.includedSlides.intro.title,
  }),
  ...visibleSlide({
    slide: agendaSlide([
      { title: 'The Who', text: ' - Audience' },
      { title: 'The What', text: ' - Creative' },
      { title: 'The Where', text: ' - Channels' },
    ]),
    visible: options.customFields.includedSlides.intro.agenda,
  }),
  ...visibleSlide({
    slide: howWeDoItSlide(),
    visible: options.customFields.includedSlides.intro.howWeDoIt,
  }),
  ...buildSalesSlides(options),
  ...buildVisitsSlides(options),
  ...campaignInsightsSlides(
    options.campaignAnalyticsReport,
    options.customFields.includedSlides.campaignInsights
  ),
];
